import { Point, Rect, Size } from '@geom';
import { hasAnyKeys, immerable } from '@utils';

import { LayoutElement } from './LayoutElement';
import type { ElementId } from './types';

export class SnorfLayout {
  [immerable] = true;

  static readonly ROOT_ID = '_root' as ElementId;

  layoutElements = new Map<ElementId, LayoutElement>();

  get allElements(): LayoutElement[] {
    return Array.from(this.layoutElements.values());
  }

  layoutElement(id: string | ElementId, index?: number): LayoutElement | undefined {
    const fullId = (index === undefined ? id : `${id}-${index}`) as ElementId;
    return this.layoutElements.get(fullId);
  }

  getLayoutElement(id: string | ElementId, index?: number): LayoutElement {
    const fullId = (index === undefined ? id : `${id}-${index}`) as ElementId;
    const layoutElement = this.layoutElements.get(fullId);
    if (!layoutElement) {
      throw new Error(`Layout element with id ${fullId} not found`);
    }
    return layoutElement;
  }

  addLayoutElement(layoutElement: LayoutElement): this {
    if (hasAnyKeys(this.layoutElements, [layoutElement.id])) {
      throw new Error(`Layout element with id ${layoutElement.id} already exists`);
    }
    this.layoutElements.set(layoutElement.id, layoutElement);

    return this;
  }

  get bounds(): Rect {
    const rootLE = this.getLayoutElement(SnorfLayout.ROOT_ID);
    return rootLE.bounds;
  }

  get center(): Point {
    return this.bounds.center;
  }

  set center(p: Point) {
    const rootLE = this.getLayoutElement(SnorfLayout.ROOT_ID);
    rootLE.bounds = rootLE.bounds.settingCenter(p);
  }

  get size(): Size {
    const rootLE = this.getLayoutElement(SnorfLayout.ROOT_ID);
    return rootLE.dimensions;
  }

  get opacity(): number {
    const rootLE = this.getLayoutElement(SnorfLayout.ROOT_ID);
    return rootLE.opacity;
  }

  set opacity(value: number) {
    const rootLE = this.getLayoutElement(SnorfLayout.ROOT_ID);
    rootLE.opacity = value;
  }

  getChildren(parentId: string | ElementId): LayoutElement[] {
    const parent = this.layoutElement(parentId);
    if (!parent) {
      throw new Error(`Parent layout element with id ${parentId} not found`);
    }
    return parent.childIds.map(childId => this.getLayoutElement(childId));
  }

  getParent(elementId: string | ElementId): LayoutElement | undefined {
    const element = this.layoutElement(elementId);
    if (!element) {
      throw new Error(`Layout element with id ${elementId} not found`);
    }
    if (element.parentId === undefined) {
      return undefined;
    }
    return this.layoutElements.get(element.parentId);
  }
}
